import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Calendar, Plus, Edit, Trash2, Loader2, Power } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';

const EscalasPage = () => {
  const { api, user } = useAuth();
  const [escalas, setEscalas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEscala, setSelectedEscala] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    tipo: '5x2',
    horas_diarias: 8,
    horas_semanais: 44,
    horas_mensais: 220,
    tolerancia_minutos: 10,
    horario_entrada: '08:00',
    horario_saida: '17:00',
    intervalo_inicio: '12:00',
    intervalo_fim: '13:00',
    empresa_id: user?.empresa_id || ''
  });

  const tiposEscala = [
    { value: '5x2', label: '5x2 - Seg a Sex' },
    { value: '6x1', label: '6x1 - Seg a Sáb' },
    { value: '6x2', label: '6x2 - 6 trabalho + 1 folga + 1 compensado' },
    { value: '12x36', label: '12x36 - Trabalha 12h, folga 36h' },
    { value: 'turnos_rotativos', label: 'Turnos Rotativos' }
  ];

  useEffect(() => {
    fetchEscalas();
  }, []);

  const fetchEscalas = async () => {
    try {
      const response = await api.get('/escalas');
      setEscalas(response.data);
    } catch (error) {
      console.error('Error fetching escalas:', error);
      toast.error('Erro ao carregar escalas');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/escalas', formData);
      toast.success('Escala criada com sucesso!');
      setDialogOpen(false);
      resetForm();
      fetchEscalas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar escala');
    }
  };

  const resetForm = () => {
    setFormData({
      nome: '',
      tipo: '5x2',
      horas_diarias: 8,
      horas_semanais: 44,
      horas_mensais: 220,
      tolerancia_minutos: 10,
      horario_entrada: '08:00',
      horario_saida: '17:00',
      intervalo_inicio: '12:00',
      intervalo_fim: '13:00',
      empresa_id: user?.empresa_id || ''
    });
  };

  const openEditDialog = (escala) => {
    setSelectedEscala({
      ...escala,
      horario_entrada: escala.horario_entrada || '08:00',
      horario_saida: escala.horario_saida || '17:00',
      intervalo_inicio: escala.intervalo_inicio || '12:00',
      intervalo_fim: escala.intervalo_fim || '13:00'
    });
    setEditDialogOpen(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedEscala) return;
    
    try {
      await api.put(`/escalas/${selectedEscala.id}`, {
        nome: selectedEscala.nome,
        tipo: selectedEscala.tipo,
        horas_diarias: selectedEscala.horas_diarias,
        horas_semanais: selectedEscala.horas_semanais,
        horas_mensais: selectedEscala.horas_mensais,
        tolerancia_minutos: selectedEscala.tolerancia_minutos,
        horario_entrada: selectedEscala.horario_entrada,
        horario_saida: selectedEscala.horario_saida,
        intervalo_inicio: selectedEscala.intervalo_inicio,
        intervalo_fim: selectedEscala.intervalo_fim
      });
      toast.success('Escala atualizada com sucesso!');
      setEditDialogOpen(false);
      setSelectedEscala(null);
      fetchEscalas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar escala');
    }
  };

  const handleDesativar = async (escalaId) => {
    try {
      await api.delete(`/escalas/${escalaId}`);
      toast.success('Escala desativada com sucesso!');
      fetchEscalas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar escala');
    }
  };

  const getTipoLabel = (tipo) => {
    const found = tiposEscala.find(t => t.value === tipo);
    return found ? found.label : tipo;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="escalas-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-primary font-['Outfit'] mb-1">Escalas</h2>
          <p className="text-muted-foreground">Configure as escalas de trabalho</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-accent hover:bg-accent/90" data-testid="nova-escala-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nova Escala
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">Criar Escala</DialogTitle>
              <DialogDescription>Configure os parâmetros da escala de trabalho</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome da Escala *</Label>
                  <Input
                    id="nome"
                    value={formData.nome}
                    onChange={(e) => setFormData({...formData, nome: e.target.value})}
                    placeholder="Ex: Administrativo"
                    required
                    data-testid="input-nome-escala"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tipo">Tipo *</Label>
                  <Select
                    value={formData.tipo}
                    onValueChange={(value) => setFormData({...formData, tipo: value})}
                  >
                    <SelectTrigger data-testid="select-tipo">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposEscala.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="horario_entrada">Horário Entrada *</Label>
                  <Input
                    id="horario_entrada"
                    type="time"
                    value={formData.horario_entrada}
                    onChange={(e) => setFormData({...formData, horario_entrada: e.target.value})}
                    required
                    data-testid="input-horario-entrada"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="horario_saida">Horário Saída *</Label>
                  <Input
                    id="horario_saida"
                    type="time"
                    value={formData.horario_saida}
                    onChange={(e) => setFormData({...formData, horario_saida: e.target.value})}
                    required
                    data-testid="input-horario-saida"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intervalo_inicio">Início Intervalo</Label>
                  <Input
                    id="intervalo_inicio"
                    type="time"
                    value={formData.intervalo_inicio}
                    onChange={(e) => setFormData({...formData, intervalo_inicio: e.target.value})}
                    data-testid="input-intervalo-inicio"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="intervalo_fim">Fim Intervalo</Label>
                  <Input
                    id="intervalo_fim"
                    type="time"
                    value={formData.intervalo_fim}
                    onChange={(e) => setFormData({...formData, intervalo_fim: e.target.value})}
                    data-testid="input-intervalo-fim"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="horas_diarias">Horas Diárias *</Label>
                  <Input
                    id="horas_diarias"
                    type="number"
                    step="0.5"
                    value={formData.horas_diarias}
                    onChange={(e) => setFormData({...formData, horas_diarias: parseFloat(e.target.value)})}
                    required
                    data-testid="input-horas-diarias"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="horas_semanais">Horas Semanais *</Label>
                  <Input
                    id="horas_semanais"
                    type="number"
                    step="0.5"
                    value={formData.horas_semanais}
                    onChange={(e) => setFormData({...formData, horas_semanais: parseFloat(e.target.value)})}
                    required
                    data-testid="input-horas-semanais"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="horas_mensais">Horas Mensais *</Label>
                  <Input
                    id="horas_mensais"
                    type="number"
                    step="0.5"
                    value={formData.horas_mensais}
                    onChange={(e) => setFormData({...formData, horas_mensais: parseFloat(e.target.value)})}
                    required
                    data-testid="input-horas-mensais"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tolerancia">Tolerância (min) *</Label>
                  <Input
                    id="tolerancia"
                    type="number"
                    value={formData.tolerancia_minutos}
                    onChange={(e) => setFormData({...formData, tolerancia_minutos: parseInt(e.target.value)})}
                    required
                    data-testid="input-tolerancia"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" className="bg-accent hover:bg-accent/90" data-testid="submit-escala-btn">
                  Criar Escala
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Dialog de Edição */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">Editar Escala</DialogTitle>
            <DialogDescription>Altere os parâmetros da escala de trabalho</DialogDescription>
          </DialogHeader>
          {selectedEscala && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Nome da Escala</Label>
                  <Input
                    value={selectedEscala.nome}
                    onChange={(e) => setSelectedEscala({...selectedEscala, nome: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={selectedEscala.tipo}
                    onValueChange={(value) => setSelectedEscala({...selectedEscala, tipo: value})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tiposEscala.map(tipo => (
                        <SelectItem key={tipo.value} value={tipo.value}>
                          {tipo.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Horário Entrada</Label>
                  <Input
                    type="time"
                    value={selectedEscala.horario_entrada}
                    onChange={(e) => setSelectedEscala({...selectedEscala, horario_entrada: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horário Saída</Label>
                  <Input
                    type="time"
                    value={selectedEscala.horario_saida}
                    onChange={(e) => setSelectedEscala({...selectedEscala, horario_saida: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Início Intervalo</Label>
                  <Input
                    type="time"
                    value={selectedEscala.intervalo_inicio}
                    onChange={(e) => setSelectedEscala({...selectedEscala, intervalo_inicio: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Fim Intervalo</Label>
                  <Input
                    type="time"
                    value={selectedEscala.intervalo_fim}
                    onChange={(e) => setSelectedEscala({...selectedEscala, intervalo_fim: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horas Diárias</Label>
                  <Input
                    type="number"
                    value={selectedEscala.horas_diarias}
                    onChange={(e) => setSelectedEscala({...selectedEscala, horas_diarias: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horas Semanais</Label>
                  <Input
                    type="number"
                    value={selectedEscala.horas_semanais}
                    onChange={(e) => setSelectedEscala({...selectedEscala, horas_semanais: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Horas Mensais</Label>
                  <Input
                    type="number"
                    value={selectedEscala.horas_mensais}
                    onChange={(e) => setSelectedEscala({...selectedEscala, horas_mensais: parseInt(e.target.value)})}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tolerância (min)</Label>
                  <Input
                    type="number"
                    value={selectedEscala.tolerancia_minutos}
                    onChange={(e) => setSelectedEscala({...selectedEscala, tolerancia_minutos: parseInt(e.target.value)})}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancelar</Button>
                <Button className="bg-accent hover:bg-accent/90" onClick={handleEditSubmit}>
                  Salvar Alterações
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Card className="border-slate-100">
        <CardContent className="pt-6">
          {escalas.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
              <p className="text-muted-foreground">Nenhuma escala cadastrada</p>
              <Button className="mt-4 bg-accent hover:bg-accent/90" onClick={() => setDialogOpen(true)}>
                Criar primeira escala
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nome</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Horário</TableHead>
                    <TableHead>Horas/Dia</TableHead>
                    <TableHead>Tolerância</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="w-12"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {escalas.map((escala) => (
                    <TableRow key={escala.id} data-testid={`escala-row-${escala.id}`}>
                      <TableCell className="font-medium">{escala.nome}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{getTipoLabel(escala.tipo)}</Badge>
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        {escala.horario_entrada} - {escala.horario_saida}
                      </TableCell>
                      <TableCell>{escala.horas_diarias}h</TableCell>
                      <TableCell>{escala.tolerancia_minutos} min</TableCell>
                      <TableCell>
                        <Badge variant={escala.ativa ? 'default' : 'secondary'} className={escala.ativa ? 'bg-accent' : ''}>
                          {escala.ativa ? 'Ativa' : 'Inativa'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(escala)}>
                              <Edit className="w-4 h-4 mr-2" />
                              Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-destructive"
                              onClick={() => handleDesativar(escala.id)}
                            >
                              <Power className="w-4 h-4 mr-2" />
                              Desativar
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EscalasPage;
